import { FormElementEnum, RICSchema } from '../ricFormTypes';
import { ProductCategory } from '../../penTypes';
import { FinalFeeType } from '../../financingTypes';

const OHRICSchema: RICSchema = {
  metadata: {
    feeTypeList: [
      FinalFeeType.DOCUMENT_PROCESSING,
      FinalFeeType.GOVERNMENT_FEES_TAX,
      FinalFeeType.GOVERNMENT_REGISTRATION,
      FinalFeeType.REGISTRATION_CONVENIENCE,
      FinalFeeType.FINANCED_LIEN_NOTATION,
      FinalFeeType.TOTAL_GOVERNMENT_CERTIFICATE_TITLE,
    ],
    numGapProducts: 1,
    numOtherDownpaymentElements: 3,
    numServiceContracts: 13,
    numTradeIns: 1,
    rebateAsOtherCharge: true,
  },
  elements: [
    {
      type: FormElementEnum.FORM_TITLE,
      title: 'Itemization of Amount Financed',
    },
    {
      type: FormElementEnum.SECTION,
      title: 'Vehicle Information',
      sectionElements: [
        {
          type: FormElementEnum.STRING_INPUT,
          title: 'Stock',
          responseDataField: 'carStock',
          required: true,
        },
        {
          type: FormElementEnum.INTEGER_INPUT,
          title: 'Mileage',
          responseDataField: 'mileage',
          required: true,
        },
      ],
    },
    {
      type: FormElementEnum.SECTION,
      title: 'Cash Price of vehicle, accessories, and taxes',
      sumValue: {
        responseDataFields: [
          'effectivePrice',
          'accessoriesPrice',
          'taxAmountOverride',
        ],
      },
      sectionElements: [
        {
          type: FormElementEnum.PRICE_INPUT,
          title: 'Cash Price of Motor Vehicle',
          responseDataField: 'effectivePrice',
          required: true,
        },
        {
          type: FormElementEnum.PRICE_INPUT,
          title: 'Cash Price of Accessories',
          responseDataField: 'accessoriesPrice',
        },
        {
          type: FormElementEnum.PRICE_INPUT,
          title: 'Sales Tax',
          subtitle: '(On Taxable Items Listed Above)',
          responseDataField: 'taxAmountOverride',
        },
      ],
    },
    {
      type: FormElementEnum.SECTION,
      title: 'Other charges including amounts paid to others on your behalf',
      sumValue: {
        feeTypes: [
          FinalFeeType.GOVERNMENT_FEES_TAX,
          FinalFeeType.GOVERNMENT_REGISTRATION,
          FinalFeeType.TOTAL_GOVERNMENT_CERTIFICATE_TITLE,
          FinalFeeType.DOCUMENT_PROCESSING,
          FinalFeeType.REGISTRATION_CONVENIENCE,
        ],
        productCategories: [
          ProductCategory.SURFACE_PROTECTION_PRODUCT,
          ProductCategory.THEFT_DETERRENT_DEVICE,
          ProductCategory.SERVICE_CONTRACT,
          ProductCategory.GAP,
        ],
        downPaymentPayoff: true,
      },
      sectionElements: [
        {
          type: FormElementEnum.FEE_INPUT,
          title: 'Government taxes not included in cash price',
          feeType: FinalFeeType.GOVERNMENT_FEES_TAX,
        },
        {
          type: FormElementEnum.FEE_TABLE,
          title: 'Government registration fees',
          displayTotalValue: false,
          feeType: FinalFeeType.GOVERNMENT_REGISTRATION,
          columns: [
            {
              width: '75%',
              fieldName: 'description',
            },
          ],
        },
        {
          type: FormElementEnum.FEE_INPUT,
          title: 'Government Certificate of Title Fees',
          subtitle: 'Including lien notation fee',
          feeType: FinalFeeType.TOTAL_GOVERNMENT_CERTIFICATE_TITLE,
        },
        {
          type: FormElementEnum.FEE_INPUT,
          title: 'Lien notation fee',
          feeType: FinalFeeType.FINANCED_LIEN_NOTATION,
        },
        {
          type: FormElementEnum.SUBSECTION,
          title: 'Net trade-in payoff',
          sumValue: {
            downPaymentPayoff: true,
          },
        },
        {
          type: FormElementEnum.PRODUCT_TABLE,
          title: 'Optional Gap Contract',
          displayTotalValue: true,
          productCategory: ProductCategory.GAP,
          columns: [
            {
              title: 'Name',
              width: '40%',
              fieldName: 'description',
            },
            {
              title: 'Term (months)',
              width: '20%',
              fieldName: 'months',
            },
          ],
        },
        {
          type: FormElementEnum.FEE_INPUT,
          title: 'Documentary Fee',
          feeType: FinalFeeType.DOCUMENT_PROCESSING,
        },
        {
          type: FormElementEnum.FEE_INPUT,
          title: 'Registration convenience fee',
          feeType: FinalFeeType.REGISTRATION_CONVENIENCE,
        },
        {
          type: FormElementEnum.PRODUCT_TABLE,
          title: 'Other Charges',
          subtitle: '(Seller must identify who is paid and describe purpose)',
          displayTotalValue: true,
          productCategory: ProductCategory.SERVICE_CONTRACT,
          columns: [
            {
              title: 'To',
              width: '40%',
              fieldName: 'paidTo',
            },
            {
              title: 'For',
              width: '40%',
              fieldName: 'description',
            },
          ],
        },
      ],
    },
    {
      type: FormElementEnum.LINE_ITEM,
      title: 'Total Cash Price',
      sumValue: {
        responseDataFields: [
          'effectivePrice',
          'accessoriesPrice',
          'taxAmountOverride',
        ],
        feeTypes: [
          FinalFeeType.GOVERNMENT_FEES_TAX,
          FinalFeeType.GOVERNMENT_REGISTRATION,
          FinalFeeType.TOTAL_GOVERNMENT_CERTIFICATE_TITLE,
          FinalFeeType.DOCUMENT_PROCESSING,
          FinalFeeType.REGISTRATION_CONVENIENCE,
        ],
        productCategories: [
          ProductCategory.SURFACE_PROTECTION_PRODUCT,
          ProductCategory.THEFT_DETERRENT_DEVICE,
          ProductCategory.GAP,
          ProductCategory.SERVICE_CONTRACT,
        ],
        downPaymentPayoff: true,
      },
    },
    {
      type: FormElementEnum.SECTION,
      title: 'Total Downpayment',
      sumValue: {
        downPaymentWithMinZero: true,
      },
      sectionElements: [
        {
          type: FormElementEnum.TRADE_IN_INPUT,
          title: 'Trade-in Vehicle Year',
          tradeInField: 'year',
          required: true,
        },
        {
          type: FormElementEnum.TRADE_IN_INPUT,
          title: 'Trade-in Vehicle Make',
          tradeInField: 'make',
          required: true,
        },
        {
          type: FormElementEnum.TRADE_IN_INPUT,
          title: 'Trade-in Vehicle Model',
          tradeInField: 'model',
          required: true,
        },
        {
          type: FormElementEnum.TRADE_IN_INPUT,
          title: 'Gross Trade-In Allowance',
          subtitle: '(see trade-in vehicle)',
          tradeInField: 'tradeInAllowance',
          required: true,
        },
        {
          type: FormElementEnum.TRADE_IN_INPUT,
          title: 'Payoff Amount',
          tradeInField: 'payOff',
          required: true,
        },
        {
          type: FormElementEnum.TRADE_IN_INPUT,
          title: 'Less Pay Off Made By Seller To',
          tradeInField: 'currentLienHolder',
          required: true,
        },
        {
          type: FormElementEnum.TRADE_IN_INPUT,
          title: 'Equals Net Trade-In',
          tradeInField: 'netTrade',
          required: true,
        },
        {
          type: FormElementEnum.PRICE_INPUT,
          title: 'Cash',
          responseDataField: 'downPayment',
        },
        {
          type: FormElementEnum.CHARGE_TABLE,
          title: 'Other',
          displayTotalValue: false,
          responseDataField: 'otherDownPayments',
          columns: [
            {
              width: '75%',
              fieldName: 'description',
            },
          ],
        },
      ],
    },
    {
      type: FormElementEnum.LINE_ITEM,
      title: 'Amount Financed',
      responseDataField: 'financeAmount',
    },
  ],
};

export default OHRICSchema;
